import { LegalSourceReviewPage } from '@/pages/platform/LegalSourceReviewPage'
import { BusinessLegalSubjectReviewPage } from '@/pages/platform/BusinessLegalSubjectReviewPage'
import { LegalSubjectReviewPage } from '@/pages/platform/LegalSubjectReviewPage'
import { Routes, Route, Navigate } from 'react-router-dom'
import type { ReactNode } from 'react'

// Auth
import { AuthProvider } from '@/contexts/AuthContext'
import {
  ProtectedRoute,
  UnauthorizedPage,
  RequireRouteAccess,
} from '@/components/auth/ProtectedRoute'
import VaultPage from '@/pages/vault/VaultPage'
import FirmSwitchesPage from '@/pages/vault/FirmSwitchesPage'
import { ClaimVaultPage, VaultAccountPage } from '@/pages/vault/VaultAccountPage'
import LoginPage from '@/pages/auth/LoginPage'
import IndependentAccountPage from '@/pages/auth/IndependentAccountPage'
import FirmRegistrationPage from '@/pages/auth/FirmRegistrationPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'
import CaseInviteRedemptionPage from '@/pages/auth/CaseInviteRedemptionPage'
import CaseAccessWorkspacePage from '@/pages/client/CaseAccessWorkspacePage'

// Layout
import AuthAppLayout from '@/components/AuthAppLayout'
import PlatformAppLayout from '@/components/PlatformAppLayout'
import ApiModeIndicator from '@/components/ApiModeIndicator'

// Pages
import ProfilePage from '@/pages/auth/ProfilePage'
import DashboardPage from '@/pages/dashboard/DashboardPage'
import HomePage from '@/pages/HomePage'
import SettingsPage from '@/pages/settings/SettingsPage'
import SecuritySettingsPage from '@/pages/settings/SecuritySettingsPage'
import TenantSettingsPage from '@/pages/settings/TenantSettingsPage'
import RulePackGovernancePage from '@/pages/settings/RulePackGovernancePage'
import BillingPage from '@/pages/billing/BillingPage'

// User Management Pages
import UsersPage from '@/pages/users/UsersPage'
import UserDetailPage from '@/pages/users/UserDetailPage'

// Session Management
import SessionsPage from '@/pages/sessions/SessionsPage'

// Audit Log
import AuditLogPage from '@/pages/audit/AuditLogPage'

// Customer Management Pages (Individual Taxpayers)
import CustomersPage from '@/pages/customers/CustomersPage'
import CustomerDetailPage from '@/pages/customers/CustomerDetailPage'

// Payments
import PaymentsPage from '@/pages/payments/PaymentsPage'

// Documents
import DocumentsPage from '@/pages/documents/DocumentsPage'
import DocumentDetailPage from '@/pages/documents/DocumentDetailPage'

// Assessments
import AssessmentDetailPage from '@/pages/customers/AssessmentDetailPage'
import AssistantWorkbenchPage from '@/pages/workbench/AssistantWorkbenchPage'
import PracticeGovernancePage from '@/pages/governance/PracticeGovernancePage'
import ClientPortalPage from '@/pages/client/ClientPortalPage'
import BusinessPortalPage from '@/pages/client/BusinessPortalPage'

// Search Page
import { SearchPage } from '@/pages/search/SearchPage'

// Public Validation Carousel (no auth)
import PublicValidation from '@/pages/PublicValidation'

// Error Pages
import NotFoundPage from '@/pages/errors/NotFoundPage'
import ForbiddenPage from '@/pages/errors/ForbiddenPage'
import {
  PlatformAuditPage,
  PlatformOverviewPage,
  PlatformTenantDetailPage,
  PlatformTenantsPage,
  PlatformUserDetailPage,
  PlatformUsersPage,
} from '@/pages/platform/PlatformPages'
import {
  PlatformMonitoringDetailPage,
  PlatformMonitoringPage,
} from '@/pages/platform/MonitoringPages'
import { PolicyGovernancePage } from '@/pages/platform/PolicyGovernancePage'
import { PlatformOperationsPage } from '@/pages/platform/OperationsPage'
import { PlatformSupportSessionsPage } from '@/pages/platform/SupportSessionsPage'
import { PlatformProfilePage, PlatformSettingsPage } from '@/pages/platform/PlatformAccountPages'
import {
  PlatformDeletionGovernancePage,
  TenantRecycleBinPage,
} from '@/pages/deletions/DeletionPages'
import ReturnCasePortfolioPage from '@/pages/assessments/ReturnCasePortfolioPage'
import ReturnCaseWorkspacePage from '@/pages/assessments/ReturnCaseWorkspacePage'
import { isReturnPreparationV2Enabled } from '@/config/runtime'

function ReturnCaseFeatureGate({ children }: { children: ReactNode }) {
  return isReturnPreparationV2Enabled() ? children : <Navigate to="/404" replace />
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/independent-account/new"
        element={<IndependentAccountPage key="enrollment" purpose="enrollment" />}
      />
      <Route path="/firm-registration" element={<FirmRegistrationPage />} />
      <Route
        path="/independent-account/recovery"
        element={<IndependentAccountPage key="recovery" purpose="recovery" />}
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/case-invite" element={<CaseInviteRedemptionPage />} />
      <Route path="/case-access/:caseId" element={<CaseAccessWorkspacePage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/validate" element={<PublicValidation />} />
      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/404" element={<NotFoundPage />} />

      <Route
        element={
          <ProtectedRoute routeAccess="vaultShell">
            <AuthAppLayout />
          </ProtectedRoute>
        }
      >
        <Route
          path="/client-vault"
          element={
            <RequireRouteAccess route="clientVault">
              <VaultPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/client-vault/account"
          element={
            <RequireRouteAccess route="clientVault">
              <VaultAccountPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/client-vault/:clientId"
          element={
            <RequireRouteAccess route="clientVault">
              <VaultPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/firm-vault"
          element={
            <RequireRouteAccess route="firmVault">
              <VaultPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/firm-vault/:clientId"
          element={
            <RequireRouteAccess route="firmVault">
              <VaultPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/firm-switches"
          element={
            <RequireRouteAccess route="firmSwitches">
              <FirmSwitchesPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/claim-vault"
          element={
            <RequireRouteAccess route="claimVault">
              <ClaimVaultPage />
            </RequireRouteAccess>
          }
        />
      </Route>
      {/* Protected Routes - Wrapped with AuthAppLayout */}
      <Route
        element={
          <ProtectedRoute routeAccess="tenantShell">
            <AuthAppLayout />
          </ProtectedRoute>
        }
      >
        <Route
          path="/"
          element={
            <RequireRouteAccess route="home">
              <HomePage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireRouteAccess route="dashboard">
              <DashboardPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/profile"
          element={
            <RequireRouteAccess route="profile">
              <ProfilePage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireRouteAccess route="settings">
              <SettingsPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/billing"
          element={
            <RequireRouteAccess route="billing">
              <BillingPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/invoices"
          element={
            <RequireRouteAccess route="billing">
              <BillingPage />
            </RequireRouteAccess>
          }
        />

        {/* User Management Routes (Owner/Admin only) */}
        <Route
          path="/users"
          element={
            <RequireRouteAccess route="users">
              <UsersPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/users/:userId"
          element={
            <RequireRouteAccess route="userDetail">
              <UserDetailPage />
            </RequireRouteAccess>
          }
        />

        {/* Session Management */}
        <Route
          path="/sessions"
          element={
            <RequireRouteAccess route="sessions">
              <SessionsPage />
            </RequireRouteAccess>
          }
        />

        {/* Audit Log (Owner/Admin only) */}
        <Route
          path="/audit-log"
          element={
            <RequireRouteAccess route="audit">
              <AuditLogPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/recycle-bin"
          element={
            <RequireRouteAccess route="recycleBin">
              <TenantRecycleBinPage />
            </RequireRouteAccess>
          }
        />

        {/* Security Settings (Owner/Admin only) */}
        <Route
          path="/settings/security"
          element={
            <RequireRouteAccess route="securitySettings">
              <SecuritySettingsPage />
            </RequireRouteAccess>
          }
        />

        {/* Organization Settings (Owner/Admin only) */}
        <Route
          path="/settings/organization"
          element={
            <RequireRouteAccess route="organizationSettings">
              <TenantSettingsPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/settings/rule-packs"
          element={
            <RequireRouteAccess route="rulePacks">
              <RulePackGovernancePage />
            </RequireRouteAccess>
          }
        />

        {/* Search Route */}
        <Route
          path="/search"
          element={
            <RequireRouteAccess route="search">
              <SearchPage />
            </RequireRouteAccess>
          }
        />

        {/* Customer Management Routes */}
        <Route
          path="/customers"
          element={
            <RequireRouteAccess route="customers">
              <CustomersPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/customers/:customerId"
          element={
            <RequireRouteAccess route="customerDetail">
              <CustomerDetailPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/customers/:customerId/assessments/:assessmentId"
          element={
            <RequireRouteAccess route="assessmentDetail">
              <AssessmentDetailPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/return-preparation-cases"
          element={
            <RequireRouteAccess route="returnCases">
              <ReturnCaseFeatureGate>
                <ReturnCasePortfolioPage />
              </ReturnCaseFeatureGate>
            </RequireRouteAccess>
          }
        />
        <Route
          path="/return-preparation-cases/:caseId/workspace"
          element={
            <RequireRouteAccess route="returnCases">
              <ReturnCaseFeatureGate>
                <ReturnCaseWorkspacePage />
              </ReturnCaseFeatureGate>
            </RequireRouteAccess>
          }
        />
        <Route
          path="/assistant-workbench"
          element={
            <RequireRouteAccess route="assistantWorkbench">
              <AssistantWorkbenchPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/practice-governance"
          element={
            <RequireRouteAccess route="practiceGovernance">
              <PracticeGovernancePage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/my-tax"
          element={
            <RequireRouteAccess route="clientPortal">
              <ClientPortalPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/my-business"
          element={
            <RequireRouteAccess route="businessPortal">
              <BusinessPortalPage />
            </RequireRouteAccess>
          }
        />

        {/* Payments */}
        <Route
          path="/payments"
          element={
            <RequireRouteAccess route="payments">
              <PaymentsPage />
            </RequireRouteAccess>
          }
        />

        {/* Documents */}
        <Route
          path="/documents"
          element={
            <RequireRouteAccess route="documents">
              <DocumentsPage variant="all" />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/documents/all"
          element={
            <RequireRouteAccess route="documents">
              <DocumentsPage variant="all" />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/documents/recent"
          element={
            <RequireRouteAccess route="documents">
              <DocumentsPage variant="recent" />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/documents/archived"
          element={
            <RequireRouteAccess route="documents">
              <DocumentsPage variant="archived" />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/documents/:documentId"
          element={
            <RequireRouteAccess route="documentDetail">
              <DocumentDetailPage />
            </RequireRouteAccess>
          }
        />
      </Route>

      <Route
        element={
          <ProtectedRoute routeAccess="platformShell">
            <PlatformAppLayout />
          </ProtectedRoute>
        }
      >
        <Route
          path="/platform"
          element={
            <RequireRouteAccess route="platformOverview">
              <PlatformOverviewPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/tenants"
          element={
            <RequireRouteAccess route="platformTenants">
              <PlatformTenantsPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/tenants/:tenantId"
          element={
            <RequireRouteAccess route="platformTenants">
              <PlatformTenantDetailPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/users"
          element={
            <RequireRouteAccess route="platformUsers">
              <PlatformUsersPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/users/:userId"
          element={
            <RequireRouteAccess route="platformUsers">
              <PlatformUserDetailPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/monitoring"
          element={
            <RequireRouteAccess route="platformMonitoring">
              <PlatformMonitoringPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/monitoring/services/:service"
          element={
            <RequireRouteAccess route="platformMonitoring">
              <PlatformMonitoringDetailPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/business-legal-control-reviews"
          element={
            <RequireRouteAccess route="platformBusinessSubjectReview">
              <BusinessLegalSubjectReviewPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/source-relationship-reviews"
          element={
            <RequireRouteAccess route="platformSubjectReview">
              <LegalSourceReviewPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/legal-control-reviews"
          element={
            <RequireRouteAccess route="platformSubjectReview">
              <LegalSubjectReviewPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/legal-control-policies"
          element={
            <RequireRouteAccess route="platformPolicyGovernance">
              <PolicyGovernancePage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/operations"
          element={
            <RequireRouteAccess route="platformOperations">
              <PlatformOperationsPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/audit"
          element={
            <RequireRouteAccess route="platformAudit">
              <PlatformAuditPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/deletions"
          element={
            <RequireRouteAccess route="platformDeletions">
              <PlatformDeletionGovernancePage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/support-sessions"
          element={
            <RequireRouteAccess route="platformSupport">
              <PlatformSupportSessionsPage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/profile"
          element={
            <RequireRouteAccess route="platformProfile">
              <PlatformProfilePage />
            </RequireRouteAccess>
          }
        />
        <Route
          path="/platform/settings"
          element={
            <RequireRouteAccess route="platformSettings">
              <PlatformSettingsPage />
            </RequireRouteAccess>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
      <ApiModeIndicator />
    </AuthProvider>
  )
}
